// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Biography from './components/Biography';
import Publications from './components/Publications';
import Conferences from './components/Conferences';
import NotFound from './components/NotFound';
// import Misc from './components/Misc';
// import Travel from './components/Travel';
import Teaching from './components/Teaching';
import ScrollToTopButton from './components/ScrollToTopButton';
import {
  buildAbsoluteUrl,
  getLanguageFromPathname,
  stripLanguagePrefix,
} from './utils/languageRoutes';
import './css/App.css';

const upsertMeta = (name, content) => {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const upsertLink = (selector, attributes) => {
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    document.head.appendChild(link);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    link.setAttribute(key, value);
  });
};

function AppContent() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const language = getLanguageFromPathname(location.pathname);
  const pagePath = stripLanguagePrefix(location.pathname);
  const [activeLanguage, setActiveLanguage] = useState(i18n.language);

  useEffect(() => {
    let isMounted = true;

    if (i18n.language !== language) {
      i18n.changeLanguage(language).then(() => {
        if (isMounted) {
          setActiveLanguage(language);
        }
      });
    } else {
      setActiveLanguage(language);
    }

    return () => {
      isMounted = false;
    };
  }, [i18n, language]);

  useEffect(() => {
    if (activeLanguage !== language) {
      return;
    }

    document.title = t('tab_title');
    document.documentElement.lang = language;
    upsertMeta('description', t('seo_description'));
    upsertLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: buildAbsoluteUrl(pagePath, language),
    });
    upsertLink('link[rel="alternate"][hreflang="en"]', {
      rel: 'alternate',
      hreflang: 'en',
      href: buildAbsoluteUrl(pagePath, 'en'),
    });
    upsertLink('link[rel="alternate"][hreflang="ru"]', {
      rel: 'alternate',
      hreflang: 'ru',
      href: buildAbsoluteUrl(pagePath, 'ru'),
    });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: 'alternate',
      hreflang: 'x-default',
      href: buildAbsoluteUrl(pagePath, 'en'),
    });
  }, [t, language, activeLanguage, pagePath]);

  if (activeLanguage !== language) {
    return null;
  }

  return (
    <div className="app-container" key={activeLanguage}>
      <Header />
      <Navbar />
      <main className="main-content container mt-5">
        <Routes>
          <Route path="/" element={<Biography />} />
          <Route path="/ru" element={<Biography />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/ru/publications" element={<Publications />} />
          <Route path="/conferences" element={<Conferences />} />
          <Route path="/ru/conferences" element={<Conferences />} />
          {/* <Route path="/misc" element={<Misc />} /> */}
          {/* <Route path="/travel" element={<Travel />} /> */}
          <Route path="/teaching" element={<Teaching />} />
          <Route path="/ru/teaching" element={<Teaching />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <ScrollToTopButton />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
